import { useCallback } from "react";
import { V2_API_URL } from "@/lib/v2-api/client";

export interface UploadedChatMedia {
  readonly id: string;
  readonly url: string;
}

/**
 * Upload a file to a chat session's media (V2, `POST /api/v2/chat-media/upload`)
 * and return the created record. Single source of the upload network logic —
 * shared by ChatWindow (needs id + url) and the mobile composer (needs id).
 */
export async function uploadChatMedia(
  file: File,
  sessionId: string,
): Promise<UploadedChatMedia> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("sessionId", sessionId);
  // Pass the MIME explicitly: the multipart part's Content-Type gets
  // re-derived from the filename extension by request interceptors
  // (e.g. Requestly) and the iOS WKWebView (.webm → video/webm,
  // .m4a → audio/x-m4a), so the server can't trust `file.type`.
  if (file.type) formData.append("mimeType", file.type);
  const token = localStorage.getItem("bearer");
  const res = await fetch(`${V2_API_URL}/api/v2/chat-media/upload`, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? `Upload failed (${res.status})`);
  }
  const media: unknown = await res.json();
  if (
    !media ||
    typeof media !== "object" ||
    typeof (media as Record<string, unknown>).id !== "string" ||
    typeof (media as Record<string, unknown>).url !== "string"
  ) {
    throw new TypeError("Upload response missing a media id or url");
  }
  return media as UploadedChatMedia;
}

/**
 * Hook wrapper returning just the created media id, for callers (e.g. the
 * mobile composer) that send a message with `mediaIds`.
 */
export function useChatMediaUpload() {
  const upload = useCallback(
    async (file: File, sessionId: string): Promise<string> =>
      (await uploadChatMedia(file, sessionId)).id,
    [],
  );

  return { upload };
}
